import { useCallback, useEffect, useState } from 'react';
import { api, call } from '../platform/api.js';
import { compressImage } from '../platform/compress.js';
import { ledger } from '../platform/browser.js';
import { uploadQueue } from '../platform/uploads.js';
import type { PhotoDraft } from './photo-strip.js';

/**
 * The capture→compress→mint→upload pipeline, extracted from the intake screen the day a
 * second consumer appeared (adding photos to an EXISTING item from its detail screen).
 * One pipeline, two screens — the alternative was a copy that would drift exactly like
 * the two error classifiers this project already refused to have.
 */

export interface UploadDraft extends PhotoDraft {
  file: File;
  key?: string;
}

export interface PhotoUploads {
  photos: UploadDraft[];
  addFiles: (files: File[]) => Promise<void>;
  removePhoto: (id: string) => void;
  retryPhoto: (id: string) => void;
  /** True when every photo reached 'done' — the save/attach gates hang off this. */
  settled: boolean;
  reset: () => void;
}

export function usePhotoUploads(): PhotoUploads {
  const [photos, setPhotos] = useState<UploadDraft[]>([]);

  useEffect(
    () =>
      uploadQueue.subscribe(() => {
        setPhotos((prev) =>
          prev.map((p) => {
            const entry = uploadQueue.entries().find((e) => e.key === p.key);
            return entry ? { ...p, status: entry.status, message: entry.message } : p;
          }),
        );
      }),
    [],
  );

  const addFiles = useCallback(async (files: File[]) => {
    const drafts: UploadDraft[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'compressing',
    }));
    setPhotos((prev) => [...prev, ...drafts]);

    const urls = await call('uploadUrls', () => api.uploadUrls.mutate({ count: drafts.length }));
    if (!urls.ok) {
      setPhotos((prev) =>
        prev.map((p) =>
          drafts.some((d) => d.id === p.id) ? { ...p, status: 'failed', message: urls.failure.message } : p,
        ),
      );
      return;
    }

    await Promise.all(
      drafts.map(async (draft, i) => {
        const upload = urls.data.uploads[i];
        if (!upload) return;
        let body: Blob;
        try {
          body = await compressImage(draft.file);
        } catch (error) {
          // A 6 MB upload that works beats a lost photo; ledgered so a browser where
          // compression never works becomes visible in diagnostics.
          ledger.record(error, 'intake:compress', { notified: false });
          body = draft.file;
        }
        setPhotos((prev) => prev.map((p) => (p.id === draft.id ? { ...p, key: upload.key, status: 'queued' } : p)));
        uploadQueue.enqueue(upload.key, upload.url, body);
      }),
    );
  }, []);

  const removePhoto = useCallback((id: string) => {
    setPhotos((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone?.key) uploadQueue.remove(gone.key);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const retryPhoto = useCallback(
    (id: string) => {
      setPhotos((prev) => {
        const photo = prev.find((p) => p.id === id);
        if (photo?.key) {
          uploadQueue.retry(photo.key);
          return prev;
        }
        // No key: the URL mint itself failed — re-run the whole add for this file.
        if (photo) {
          URL.revokeObjectURL(photo.previewUrl);
          void addFiles([photo.file]);
          return prev.filter((p) => p.id !== id);
        }
        return prev;
      });
    },
    [addFiles],
  );

  const reset = useCallback(() => {
    setPhotos((prev) => {
      for (const photo of prev) URL.revokeObjectURL(photo.previewUrl);
      return [];
    });
  }, []);

  const settled = photos.every((p) => p.status === 'done');

  return { photos, addFiles, removePhoto, retryPhoto, settled, reset };
}
